import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useLocalizedStrings } from '../../Localization';
import { BreadcrumbProps, Button, Icon, Tabs, DataTable, Card, CardBody, Badge, Tooltip, Modal, CodeDisplay, Refreshing } from '@aidkitorg/component-library';
import { AdminPanelPage } from '../AdminPanelPage';
import { ColumnDef } from '@tanstack/react-table';
import { useNotifications } from '../../Contexts/NotificationsContext';
import type { NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import { NotificationRecipientsDownloadModal } from './components/NotificationRecipientsDownloadModal';
import { NotificationRecipientsFilters, NotificationRecipientsFilterParams } from './components/filters/NotificationRecipientsFilters';
import { useNotificationDetailsConfig } from '../shared/hooks/useAdminPanelPageConfig';

export const NotificationRecipientsPage = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { targetFieldPrefix } = useParams<{ targetFieldPrefix: string }>();

  const [activeTab, setActiveTab] = useState<'sent' | 'eligible'>('sent');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState<{ kind: 'sent' | 'eligible'; recipient: NotificationRecipientsResult['sent'][number] | NotificationRecipientsResult['eligible'][number] | null; }>({ kind: 'sent', recipient: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const [sentColumnVisibility, setSentColumnVisibility] = useState<Record<string, boolean>>({});
  const [eligibleColumnVisibility, setEligibleColumnVisibility] = useState<Record<string, boolean>>({});
  const [activeFilters, setActiveFilters] = useState<NotificationRecipientsFilterParams>({});

  const {
    fetchNotificationRecipients,
    getNotificationRecipients,
    getIsRecipientsLoading,
    getRecipientsTimestamp,
  } = useNotifications();

  const recipientsData = getNotificationRecipients(targetFieldPrefix || '');
  const isLoading = getIsRecipientsLoading(targetFieldPrefix || '');
  const lastUpdatedTimestamp = getRecipientsTimestamp(targetFieldPrefix || '');

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isLoading: isConfigLoading,
    getLocalizedText,
    isVisible
  } = useNotificationDetailsConfig();

  useEffect(() => {
    if (targetFieldPrefix) {
      fetchNotificationRecipients(targetFieldPrefix);
    }
  }, [targetFieldPrefix, fetchNotificationRecipients]);

  // Set initial column visibility to hide additionalColumns by default
  useEffect(() => {
    if (!pageConfig?.additionalColumns?.targetFields) return;

    const additionalFields = pageConfig.additionalColumns.targetFields;
    const defaultFields = pageConfig.defaultTableColumns?.targetFields || [];
    const hiddenColumns: Record<string, boolean> = {};

    additionalFields.forEach((field: string) => {
      // If the field is also in defaultTableColumns.targetFields, don't hide it
      if (defaultFields.includes(field)) {
        return;
      }

      const columnId = `variable_${field}`;
      hiddenColumns[columnId] = false; // false means hidden
    });

    // Only update if there are columns to hide and they haven't been set yet
    if (Object.keys(hiddenColumns).length > 0) {
      setSentColumnVisibility(prev => ({ ...prev, ...hiddenColumns }));
      setEligibleColumnVisibility(prev => ({ ...prev, ...hiddenColumns }));
    }
  }, [pageConfig?.additionalColumns?.targetFields, pageConfig?.defaultTableColumns?.targetFields]);

  const handleForceRefresh = () => {
    if (targetFieldPrefix) {
      fetchNotificationRecipients(targetFieldPrefix, true);
    }
  };
  
  // Handle marking the variables through string matching in the front end so we can have clean message text in downloads. 
  const renderHighlightedContent = (content: string, variables: Record<string, string>): React.ReactNode => {
    if (!variables) return content;
    
    // Create value-to-key mapping, sorted by length (longest first) to avoid partial matches
    const variableEntries = Object.entries(variables)
      .filter(([key, value]) => value && String(value).trim())
      .map(([key, value]) => ({ key, value: String(value) }))
      .sort((a, b) => b.value.length - a.value.length);
    
    if (variableEntries.length === 0) return content;
    
    // Split content into parts and highlight variables
    let parts: React.ReactNode[] = [content];
    let partIndex = 0;
    
    variableEntries.forEach(({ key, value }) => {
      const newParts: React.ReactNode[] = [];
      
      parts.forEach((part, index) => {
        if (typeof part !== 'string') {
          newParts.push(part);
          return;
        }
        
        // Escape special regex characters in the value
        const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escapedValue}\\b`, 'gi');
        
        const segments = part.split(regex);
        const matches = part.match(regex) || [];
        
        segments.forEach((segment, segIndex) => {
          if (segment) newParts.push(segment);
          if (segIndex < matches.length) {
            newParts.push(
              <Tooltip key={`${partIndex}-${segIndex}`} text={key}>
                <span className="bg-gray-100 text-mono break-all">
                  {matches[segIndex]}
                </span>
              </Tooltip>
            );
          }
        });
      });
      
      parts = newParts;
      partIndex++;
    });
    
    return <>{parts}</>;
  };

  const openPreview = async (kind: 'sent' | 'eligible', recipient: NotificationRecipientsResult['sent'][number] | NotificationRecipientsResult['eligible'][number]) => {
    setPreviewState({ kind, recipient });
    setPreviewOpen(true);
  }

  // Extract available statuses and channels from the data
  const { availableStatuses, availableChannels } = useMemo(() => {
    const statuses = new Set<string>();
    const channels = new Set<string>();

    recipientsData?.sent?.forEach(recipient => {
      if (recipient.status) statuses.add(recipient.status);
      if (recipient.channel) channels.add(recipient.channel);
    });

    recipientsData?.eligible?.forEach(recipient => {
      if (recipient.status) statuses.add(recipient.status);
      if (recipient.channel) channels.add(recipient.channel);
    });

    return {
      availableStatuses: Array.from(statuses).sort(),
      availableChannels: Array.from(channels).sort(),
    };
  }, [recipientsData]);

  // Filter the data based on active filters
  const filteredSentData = useMemo(() => {
    if (!recipientsData?.sent) return [];

    return recipientsData.sent.filter(recipient => {
      // Filter by status
      if (activeFilters.statuses && activeFilters.statuses.length > 0) {
        if (!activeFilters.statuses.includes(recipient.status)) return false;
      }

      // Filter by channel
      if (activeFilters.channels && activeFilters.channels.length > 0) {
        if (!activeFilters.channels.includes(recipient.channel)) return false;
      }

      return true;
    });
  }, [recipientsData?.sent, activeFilters]);

  const filteredEligibleData = useMemo(() => {
    if (!recipientsData?.eligible) return [];

    return recipientsData.eligible.filter(recipient => {
      // Filter by status
      if (activeFilters.statuses && activeFilters.statuses.length > 0) {
        if (!activeFilters.statuses.includes(recipient.status)) return false;
      }

      // Filter by channel
      if (activeFilters.channels && activeFilters.channels.length > 0) {
        if (!activeFilters.channels.includes(recipient.channel)) return false;
      }

      return true;
    });
  }, [recipientsData?.eligible, activeFilters]);

  // Handle filter changes
  const handleFiltersChange = useCallback((filters: NotificationRecipientsFilterParams) => {
    setActiveFilters(filters);
  }, []);

  // Create columns with dynamic variable columns
  const sentColumns = useMemo((): ColumnDef<NotificationRecipientsResult['sent'][number]>[] => {
    const baseColumns: ColumnDef<NotificationRecipientsResult['sent'][number]>[] = [
      {
        accessorKey: 'uid',
        id: 'uid',
        header: 'UID',
        enablePinning: true,
        cell: ({ row }) => {
          const uid = row.original.uid
          const [copied, setCopied] = useState(false)
      
          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(uid)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            } catch (err) {
              console.error("Failed to copy: ", err)
            }
          }
      
          return (
            <div className="flex items-center gap-2">
              <span className="truncate">{uid}</span>
              <Button 
                icon={
                  <Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>
                }
                variant="secondary" 
                size="sm"
                aria-label={L.admin_panel.payments.uuid_desc}
                onClick={handleCopy}
              />
            </div>
          )
        }
      },
      {
        accessorKey: 'legal_name',
        id: 'legal_name',
        header: L.admin_panel.notifications.legal_name,
      },
      {
        accessorKey: 'channel',
        id: 'channel',
        header: L.admin_panel.notifications.channel,
        cell: ({ row }) => {
          const channel = row.getValue('channel') as string;
          let iconName = 'EnvelopeIcon';
          if (channel === 'sms') iconName = 'DevicePhoneMobileIcon';
          if (channel === 'whatsapp') iconName = 'WhatsAppIcon';
          
          return (
            <div className="flex items-center gap-2">
              <Icon name={iconName as any} className="w-4 h-4" />
              {channel}
            </div>
          );
        }
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: L.admin_panel.notifications.status,
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          let variant: 'success' | 'error' | 'warning' = 'success';
          if (status === 'failed') variant = 'error';
          if (status === 'pending') variant = 'warning';
          
          return (
            <div className="flex items-center gap-2">
              <Badge variant={variant}>{status}</Badge>
              {row.original.failureReason && (
                <Tooltip text={row.original.failureReason}>
                  <Icon name="ExclamationCircleIcon" className="w-4 h-4 text-red-500" />
                </Tooltip>
              )}
              {row.original.attemptNumber && (
                <Tooltip text={`Attempt ${row.original.attemptNumber}`}>
                  <Icon name="ClockIcon" className="w-4 h-4 text-gray-500" />
                </Tooltip>
              )}
            </div>
          );
        }
      },
    ];

    // Add dynamic variable columns for sent recipients
    if (recipientsData?.variables?.length) {
      // Create columns for each variable
      recipientsData?.variables?.sort().forEach(variable => {
        baseColumns.push({
          accessorFn: (row) => row.variables?.[variable],
          id: `variable_${variable}`,
          header: variable,
          cell: ({ row }) => {
            const value = row.original.variables?.[variable];
            if (value === undefined || value === null || value === '') {
              return <span className="text-gray-400 italic">--</span>;
            }
            
            return <CodeDisplay className='text-sm' copyable={false} displayMode="block" code={value} contentMode="wrap" variant="gray" />;
          }
        });
      });
    }

    // Add the remaining base columns
    baseColumns.push(
      {
        accessorKey: 'sentTimestamp',
        id: 'sentTimestamp',
        header: L.admin_panel.notifications.sent_timestamp,
        cell: ({ row }) => {
          const timestamp = row.getValue('sentTimestamp') as string;
          if (!timestamp) return '--';
          
          const date = new Date(timestamp);
          return date.toLocaleString('en-US', {
            timeZone: 'UTC',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
        }
      },
      {
        id: 'preview',
        header: L.admin_panel.notifications.preview,
        size: 120,
        enablePinning: true,
        cell: ({ row }) => (
          <Button size="sm" onClick={() => openPreview('sent', row.original)} icon={<Icon name="EyeIcon" />}>
            {L.admin_panel.reports.view}
          </Button>
        )
      }
    );

    return baseColumns;
  }, [recipientsData?.variables, L]);

  const eligibleColumns = useMemo((): ColumnDef<NotificationRecipientsResult['eligible'][number]>[] => {
    const baseColumns: ColumnDef<NotificationRecipientsResult['eligible'][number]>[] = [
      {
        accessorKey: 'uid',
        id: 'uid',
        header: 'UID',
        cell: ({ row }) => {
          const uid = row.original.uid
          const [copied, setCopied] = useState(false)
      
          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(uid)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            } catch (err) {
              console.error("Failed to copy: ", err)
            }
          }
      
          return (
            <div className="flex items-center gap-2">
              <span className="truncate">{uid}</span>
              <Button 
                icon={
                  <Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>
                }
                variant="secondary" 
                size="sm"
                aria-label={L.admin_panel.payments.uuid_desc}
                onClick={handleCopy}
              />
            </div>
          )
        }

      },
      {
        accessorKey: 'legal_name',
        id: 'legal_name',
        header: L.admin_panel.notifications.legal_name,
      },
      {
        accessorKey: 'channel',
        id: 'channel',
        header: L.admin_panel.notifications.channel,
        cell: ({ row }) => {
          const channel = row.getValue('channel') as string;
          let iconName = 'EnvelopeIcon';
          if (channel === 'sms') iconName = 'DevicePhoneMobileIcon';
          if (channel === 'whatsapp') iconName = 'WhatsAppIcon';
          
          return (
            <div className="flex items-center gap-2">
              <Icon name={iconName as any} className="w-4 h-4" />
              {channel}
            </div>
          );
        }
      }
    ];

    // Add dynamic variable columns for eligible recipients
    if (recipientsData?.variables?.length) {
      // Create columns for each variable
      recipientsData?.variables.sort().forEach(variable => {
        baseColumns.push({
          accessorFn: (row) => row.variables?.[variable],
          id: `variable_${variable}`,
          header: variable,
          cell: ({ row }) => {
            const value = row.original.variables?.[variable];
            if (value === undefined || value === null || value === '') {
              return <span className="text-gray-400 italic">--</span>;
            }

            return <CodeDisplay className='text-sm' copyable={false} displayMode="block" code={value} contentMode="wrap" variant="gray" />;
          }
        });
      });
    }

    // Add preview button at the end
    baseColumns.push({
      id: 'preview',
      header: L.admin_panel.notifications.preview,
      cell: ({ row }) => (
        <Button size="sm" onClick={() => openPreview('eligible', row.original)} icon={<Icon name="EyeIcon" />}>
          {L.admin_panel.notifications.preview}
        </Button> 
      )
    });

    return baseColumns;
  }, [recipientsData?.variables, L]);

  // Use config to control UI elements
  const enableSearch = isVisible(['searchBar']);
  const showExportButton = isVisible(['exportButton']);
  const showSentTab = isVisible(['tabConfiguration', 'sent']);
  const showEligibleTab = isVisible(['tabConfiguration', 'eligible']);
  const showTabs = showSentTab || showEligibleTab;
  const pageDescription = getLocalizedText(['pageHeader', 'description'], L.admin_panel.notifications.notification_recipients_desc);

  // Determine if there are any available filters based on pageConfig
  const hasAvailableFilters = useMemo(() => {
    if (!pageConfig?.defaultTableColumns) {
      // If no config, all filters are available by default
      return true;
    }

    const columns = pageConfig.defaultTableColumns;
    // Check if at least one filterable column is visibleAndFilterable
    return columns.status === 'visibleAndFilterable' ||
           columns.channel === 'visibleAndFilterable';
  }, [pageConfig]);

  // Filter columns based on config
  const filteredSentColumns = useMemo(() => {
    if (!pageConfig?.defaultTableColumns) {
      return sentColumns;
    }

    const config = pageConfig.defaultTableColumns;
    const additionalFields = pageConfig.additionalColumns?.targetFields || [];

    return sentColumns.filter((column) => {
      const id = column.id as string;

      // Always show these columns
      if (id === 'uid' || id === 'legal_name' || id === 'preview') return true;

      // Check config for other columns
      if (id === 'channel') return config.channel !== 'hidden';
      if (id === 'status') return config.status !== 'hidden';
      if (id === 'sentTimestamp') return config.sent !== 'hidden';

      // Show dynamic variable columns if they're in targetFields or additionalColumns
      if (id?.startsWith('variable_')) {
        // Extract the variable name from the column id (remove 'variable_' prefix)
        const variableName = id.substring('variable_'.length);

        // Don't show if it's already a base column (legal_name or uid)
        if (variableName === 'legal_name' || variableName === 'uid') {
          return false;
        }

        // Show if it's in defaultTableColumns.targetFields OR additionalColumns.targetFields
        const isInDefaultFields = config.targetFields?.includes(variableName) || false;
        const isInAdditionalFields = additionalFields.includes(variableName);

        return isInDefaultFields || isInAdditionalFields;
      }

      return true;
    });
  }, [sentColumns, pageConfig]);

  const filteredEligibleColumns = useMemo(() => {
    if (!pageConfig?.defaultTableColumns) {
      return eligibleColumns;
    }

    const config = pageConfig.defaultTableColumns;
    const additionalFields = pageConfig.additionalColumns?.targetFields || [];

    return eligibleColumns.filter((column) => {
      const id = column.id as string;

      // Always show these columns
      if (id === 'uid' || id === 'legal_name' || id === 'preview') return true;

      // Check config for other columns
      if (id === 'channel') return config.channel !== 'hidden';

      // Show dynamic variable columns if they're in targetFields or additionalColumns
      if (id?.startsWith('variable_')) {
        // Extract the variable name from the column id (remove 'variable_' prefix)
        const variableName = id.substring('variable_'.length);

        // Don't show if it's already a base column (legal_name or uid)
        if (variableName === 'legal_name' || variableName === 'uid') {
          return false;
        }

        // Show if it's in defaultTableColumns.targetFields OR additionalColumns.targetFields
        const isInDefaultFields = config.targetFields?.includes(variableName) || false;
        const isInAdditionalFields = additionalFields.includes(variableName);

        return isInDefaultFields || isInAdditionalFields;
      }

      return true;
    });
  }, [eligibleColumns, pageConfig]);

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.notifications,
      href: '/admin-panel/notifications'
    },
    {
      label: recipientsData?.notificationName || L.admin_panel.notifications.notification_recipients
    }
  ];


  const pageActionButtons = [
    <Tooltip text={L.admin_panel.payments.refresh_data} asChild key="refresh">
      <Button
        size="sm"
        variant='secondary'
        onClick={handleForceRefresh}
        icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
        aria-label={L.admin_panel.payments.refresh_data}
      />
    </Tooltip>
  ];

  if (showExportButton) {
    pageActionButtons.push(
      <Tooltip text={activeTab === 'sent' ? L.admin_panel.notifications.export_sent_recipients : L.admin_panel.notifications.export_eligible_recipients} asChild key="export">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setDownloadModalOpen(true)}
        >
          <div className="flex gap-2 items-center">
            <Icon name="ArrowDownTrayIcon" role="decorative" type="solid" />
          </div>
        </Button>
      </Tooltip>
    );
  }

  return (
    <AdminPanelPage
      title={`${recipientsData?.notificationName || L.admin_panel.notifications.notification_recipients}`}
      description={pageDescription}
      breadcrumbs={breadcrumbs}
      pageActionButtons={pageActionButtons}

    >
      <div className="flex flex-col gap-4 w-full">
        {!isConfigLoading && showTabs && (<Card>
          <CardBody>
            <Tabs
              justify="left"
              selectedTab={activeTab}
              onChange={(tab) => setActiveTab(tab as 'sent' | 'eligible')}
              tabs={[
                ...(showSentTab ? [{
                  key: 'sent',
                  name: `${L.admin_panel.notifications.sent} (${filteredSentData.length})`,
                  content: (
                    <DataTable
                      className="pt-3"
                      columns={filteredSentColumns}
                      data={filteredSentData}
                      loading={isLoading}
                      pageSize={10}
                      enableSearch={enableSearch}
                      searchLabel={L.admin_panel.notifications.search_recipients}
                      lastUpdatedTimestamp={lastUpdatedTimestamp}
                      noDataMessage={L.admin_panel.notifications.empty_sent_notifications}
                      dataFilteredOutMessage={L.admin_panel.notifications.filtered_sent_notifications}
                      columnPinningState={{
                        left: ['uid']
                      }}
                      hiddenCols={Object.entries(sentColumnVisibility).filter(([_, visible]) => !visible).map(([col, _]) => col)}
                      enableColumnVisibility={true}
                      onColumnVisibilityChange={setSentColumnVisibility}
                      resizableColumns={true}
                      filters={hasAvailableFilters ? () => (
                        <NotificationRecipientsFilters
                          onFiltersChange={handleFiltersChange}
                          availableStatuses={availableStatuses}
                          availableChannels={availableChannels}
                          defaultTableColumns={pageConfig?.defaultTableColumns}
                        />
                      ) : undefined}
                    />
                  )
                }] : []),
                ...(showEligibleTab ? [{
                  key: 'eligible',
                  name: `${L.admin_panel.notifications.eligible} (${filteredEligibleData.length})`,
                  content: (
                    <DataTable
                      className="pt-2"
                      columns={filteredEligibleColumns}
                      data={filteredEligibleData}
                      loading={isLoading}
                      pageSize={10}
                      enableSearch={enableSearch}
                      searchLabel={L.admin_panel.notifications.search_recipients}
                      lastUpdatedTimestamp={lastUpdatedTimestamp}
                      noDataMessage={L.admin_panel.notifications.empty_eligible_recipients}
                      dataFilteredOutMessage={L.admin_panel.notifications.filtered_eligible_recipients}
                      columnPinningState={{
                        left: ['uid']
                      }}
                      hiddenCols={Object.entries(eligibleColumnVisibility).filter(([_, visible]) => !visible).map(([col, _]) => col)}
                      enableColumnVisibility={true}
                      onColumnVisibilityChange={setEligibleColumnVisibility}
                      resizableColumns={true}
                      filters={hasAvailableFilters ? () => (
                        <NotificationRecipientsFilters
                          onFiltersChange={handleFiltersChange}
                          availableStatuses={availableStatuses}
                          availableChannels={availableChannels}
                          defaultTableColumns={pageConfig?.defaultTableColumns}
                        />
                      ) : undefined}
                    />
                  )
                }] : [])
              ]}
            />
          </CardBody>
        </Card>
        )}
      </div>
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={previewState.kind === 'sent' ? L.admin_panel.notifications.sent_message_preview : L.admin_panel.notifications.eligible_message_preview}
        size="lg"
      >
        {
          (() => {
            const recipient = previewState.recipient;
            if (!recipient) return null;
            return (
              <Refreshing loading={previewLoading}>
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex flex-wrap gap-2 items-center text-sm text-gray-600">
                    <Badge variant="neutral">{recipient.channel}</Badge>
                    <span>UID: {recipient.uid}</span>
                    <span>
                      {recipient.legal_name}
                    </span>
                  </div>
                  {recipient?.subject && recipient?.channel === 'email' && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">{L.admin_panel.notifications.subject}</div>
                      <div className="text-sm">{recipient.subject}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">{L.admin_panel.notifications.body}</div>
                    <div className="text-sm whitespace-pre-wrap break-words border rounded p-3">
                      { recipient.status === 'failed' ? (
                        <div className="text-red-500">{recipient.failureReason || L.admin_panel.notifications.failed_to_send}</div>
                      ) : (
                        renderHighlightedContent(recipient.message || '', {...(recipient.variables || {}), ...(recipient.legal_name ? { legal_name: recipient.legal_name } : {})})
                      )}
                    </div>
                  </div>
                </div>
              </Refreshing>
            );
          })()
        }
      </Modal>
      <NotificationRecipientsDownloadModal
        notificationName={recipientsData?.notificationName || ''}
        sentData={recipientsData?.sent || []}
        eligibleData={recipientsData?.eligible || []}
        variables={recipientsData?.variables || []}
        downloadModalOpen={downloadModalOpen}
        setDownloadModalOpen={setDownloadModalOpen}
      />
    </AdminPanelPage>
  );
};